'use client';

import * as React from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { cx } from '../utils';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeClassNameProps, UnsafeStyleProps } from '../escape-hatches';
import { IS_PROD, devWarn } from '../internal/devWarn';

/* ── FieldGroup (ADR-035) ──────────────────────────────────────────
   Layout-level field-width carrier per ADR-032 Option C.

   FieldGroup is the public-API surface for cross-control field-width
   parity across the five field-backed controls (`<Input>`, `<Select>`,
   `<CreatableSelect>`, `<DatePicker>`, `<TimePicker>`). It emits one
   substrate signal — a presence/value attribute (`data-field-width-mode`)
   plus a CSS custom property (`--field-width-target`) — which six
   per-wrapper CSS rules across five controls consume to resolve their
   inline-size. The React layers of the five controls are unaware of
   FieldGroup; the wiring is CSS-only (ADR-032 §Decision Q1).

   Default rendering shape: `display: contents` (locked per ADR-032
   OQ-3 / ADR-035 §4). The wrapper does not consume a flex / grid
   track so `<FieldGroup>` inside `.j-form-grid-{2,3,4}` does not
   break grid track placement. FieldGroup emits no role / aria-* /
   landmark, so the known AT quirks around `display: contents` on
   landmark elements do not apply (ADR-032 OQ-3 resolution carries
   an AT-matrix spot-check as a Gate-4 verification — see PR).

   When `width` is omitted FieldGroup is a no-op: the substrate signal
   is not emitted (no `data-field-width-mode`, no `--field-width-target`)
   so the presence of the wrapper does not change layout. Locked per
   ADR-032 OQ-2 / ADR-035 §1.

   Nested FieldGroups: the innermost FieldGroup wins because each
   wrapper carries the substrate signal at its own scope; the field's
   per-control read-rule resolves against the closest ancestor with
   a matching `data-field-width-mode`. This is the CSS-cascade
   semantics described in ADR-035 §5. The `input-field-width-to-field-group`
   codemod (ADR-032 §Codemod) always transforms `<Input fieldWidth>`
   into a nested FieldGroup rather than skipping when an outer
   FieldGroup is already present — the innermost-wins rule preserves
   the original adopter intent.

   For non-bounded width values (`'8ch'`, `clamp(...)`, token
   references) adopters reach for ADR-017 `unsafe_style` on the
   field; FieldGroup's typed value-space stays `'fill' | 'content' |
   'auto' | number`. */

export type FieldGroupWidth = 'fill' | 'content' | 'auto' | number;

export interface FieldGroupProps
  extends StyledProps, WithChildren, UnsafeClassNameProps, UnsafeStyleProps {
  /** Field-width directive applied to any of the five field-backed
   *  controls (`<Input>`, `<Select>`, `<CreatableSelect>`,
   *  `<DatePicker>`, `<TimePicker>`) nested inside.
   *
   *   - `'fill'`     — field fills the available parent width.
   *   - `'content'`  — field hugs its intrinsic measure. Asymmetric
   *                    across the five controls by design: input-backed
   *                    controls (`<Input>`, `<TimePicker>`) hug the
   *                    native `<input>` intrinsic; trigger-button controls
   *                    (`<Select>`, `<CreatableSelect>` Select branch,
   *                    `<DatePicker>`) hug the trigger label text width
   *                    (`fit-content`). See ADR-032 §Decision Q5 + the
   *                    cross-control `'content'` story in ADR-035 §4.
   *   - `'auto'`     — field uses its CSS default; falls back to the
   *                    per-control wrapper's existing default.
   *   - `number`     — pixel value carried via `--field-width-target:
   *                    <N>px`. Capped at `max-inline-size: 100%`.
   *
   *  When omitted, FieldGroup is a pass-through (no substrate signal
   *  emitted). Locked per ADR-032 OQ-2 / ADR-035 §1. */
  width?: FieldGroupWidth;
}

/* Dev-warn dedupe scope for invalid numeric width values. Mirrors
   the Input.fieldWidth dedupe pattern at src/kit/primitives/Input.tsx:49-61.
   A first-seen invalid value warns once; every subsequent same-value
   call site is silent. */
const _seenInvalidWidth = new Set<string>();
function devWarnInvalidWidth(value: number): void {
  if (IS_PROD) return;
  const key = String(value);
  if (_seenInvalidWidth.has(key)) return;
  _seenInvalidWidth.add(key);
  devWarn(
    `[JKit.FieldGroup] received \`width={${key}}\` (must be a finite ` +
      'positive number). The prop was dropped and the field falls back to ' +
      'its per-control default. Pass a positive finite number — or one of ' +
      "`'fill' | 'content' | 'auto'` — for the intended typed path.",
  );
}

/* Resolve the public `width` value to the substrate signal pair:
   - `mode`:  the `data-field-width-mode` attribute value (omitted
              when width is undefined or invalid-numeric-fallback path)
   - `target`: the resolved CSS value for `--field-width-target` (omitted
              when the per-control wrapper doesn't need a custom-property
              read, e.g. width="auto").

   Values mirror ADR-035 §3:
     fill      → mode='fill',    target='100%'
     content   → mode='content', target='fit-content'
     auto      → mode='auto',    target undefined (per-control default)
     number(N) → mode='numeric', target='<N>px'

   Invalid numerics (NaN, Infinity, ≤0) fall back to no-signal (the
   FieldGroup behaves as if `width` had been omitted — strict invariant
   per ADR-031 precedent at src/kit/primitives/Input.tsx:98-136). */
function resolveWidth(width: FieldGroupWidth | undefined): {
  mode?: 'fill' | 'content' | 'auto' | 'numeric';
  target?: string;
} {
  if (width === undefined) return {};
  if (typeof width === 'number') {
    if (!Number.isFinite(width) || width <= 0) {
      if (!IS_PROD) devWarnInvalidWidth(width);
      return {};
    }
    return { mode: 'numeric', target: `${width}px` };
  }
  switch (width) {
    case 'fill':
      return { mode: 'fill', target: '100%' };
    case 'content':
      return { mode: 'content', target: 'fit-content' };
    case 'auto':
      return { mode: 'auto' };
  }
}

export function FieldGroup({
  width,
  children,
  className,
  unsafe_className,
  unsafe_style,
}: FieldGroupProps): ReactElement {
  const { mode, target } = resolveWidth(width);

  /* Merge the substrate custom-property carrier with any adopter-
     supplied `unsafe_style`. Adopter wins on collision (ADR-017
     escape-hatch intent) so a deliberate `unsafe_style={{
     ['--field-width-target' as any]: '50ch' }}` override takes
     precedence over the typed-prop derived value. */
  let style: CSSProperties | undefined = unsafe_style;
  if (target !== undefined) {
    style = {
      ['--field-width-target' as keyof CSSProperties]: target,
      ...(unsafe_style ?? {}),
    } as CSSProperties;
  }

  return (
    <div
      className={cx('j-field-group', className, unsafe_className)}
      data-field-width-mode={mode}
      style={style}
    >
      {children as ReactNode}
    </div>
  );
}
